import { useContext, useState } from "react";
import { AppContext } from "../contexts/app-context";
import { DEFAULT_LOCAL_STORAGE_KEY_FOR_USER_STATE } from "../constants";
import useLocalStorage from "./useLocalStorage";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface UseAuthenticationState {
  signInUser: (
    credentials: LoginRequest,
    onSuccess: (role: string) => void
  ) => void;
  resetPassword: (
    request: ForgotPasswordRequest,
    onSuccess: () => void
  ) => void;
  changePassword: (
    request: PasswordResetRequest,
    onSuccess: () => void
  ) => void;
  updatePassword: (
    request: PasswordResetRequest,
    onSuccess: () => void
  ) => void;
  signOutUser: VoidFunction;
  loading: boolean;
  error: string;
}

const useAuthentication = (): UseAuthenticationState => {
  const { setValue: setUserState, clearAll } = useLocalStorage();
  const { appState, updateAppState } = useContext(AppContext);
  const { postData, loading } = useApi();
  const { getData: getUserRoles, loading: userRolesLoading } =
    useApi<UserRoleResponse>();
  const [error, setError] = useState<string | null>();

  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const signInUser = async (
    request: LoginRequest,
    onSuccess: (role: string) => void
  ) => {
    setError(null);
    await postData(
      `${API_ROUTES.AUTHENTICATION.base}${API_ROUTES.AUTHENTICATION.login}`,
      request,
      (value) => {
        const loginResponse = value as LoginResponse;
        setUserState(DEFAULT_LOCAL_STORAGE_KEY_FOR_USER_STATE, request);

        getUserRoles(
          `${API_ROUTES.AUTHENTICATION.getUserRoles}?id=${loginResponse.user_id}`,
          (userRoleResponse) => {
            const { role } = userRoleResponse;

            updateAppState({
              ...appState,
              isUserLoggedIn: true,
              accessJWT: loginResponse.jwt,
              accessToken: loginResponse.accessToken,
              user_id: loginResponse.user_id,
              userRole: role as UserRole,
              isFirstTime: false,
              name: `${loginResponse.name.first_name} ${loginResponse.name.last_name}`,
              status: loginResponse.status,
            });
            onSuccess(userRoleResponse.role);
          },
          onFailure
        );
      },
      onFailure
    );
  };

  const changePassword = (
    request: PasswordResetRequest,
    onSuccess: () => void
  ) => {
    setError(null);
    postData(
      `${API_ROUTES.AUTHENTICATION.base}${API_ROUTES.AUTHENTICATION.changePassword}`,
      { token: request.token, password: request.password },
      onSuccess,
      onFailure
    );
  };

  const updatePassword = (
    request: PasswordResetRequest,
    onSuccess: () => void
  ) => {
    setError(null);
    postData(
      `${API_ROUTES.AUTHENTICATION.base}${API_ROUTES.AUTHENTICATION.updatePassword}`,
      { token: request.token, password: request.password },
      onSuccess,
      onFailure
    );
  };

  const resetPassword = (
    request: ForgotPasswordRequest,
    onSuccess: () => void
  ) => {
    setError(null);
    postData(
      `${API_ROUTES.AUTHENTICATION.base}${API_ROUTES.AUTHENTICATION.resetPassword}`,
      request,
      onSuccess,
      onFailure
    );
  };

  const signOutUser = () => {
    clearAll();
  };

  return {
    loading: loading || userRolesLoading,
    error,
    signInUser,
    resetPassword,
    changePassword,
    updatePassword,
    signOutUser,
  };
};

export default useAuthentication;
